import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { api } from '../services/api';
import { useShopify } from './ShopifyContext';

// =====================================================
// Types
// =====================================================

interface User {
  id: string;
  email: string;
  name: string;
  role: 'admin' | 'editor' | 'viewer';
  avatar?: string;
}

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

interface AuthContextValue extends AuthState {
  login: (email: string, password: string, remember?: boolean) => Promise<void>;
  logout: () => void;
  checkAuth: () => Promise<void>;
}

// =====================================================
// Context
// =====================================================

const AuthContext = createContext<AuthContextValue | null>(null);

const TOKEN_KEY = 'turbine_token';
const USER_KEY = 'turbine_user';

// =====================================================
// Provider
// =====================================================

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isEmbedded, isReady: shopifyReady, getSessionToken } = useShopify();

  const [state, setState] = useState<AuthState>({
    user: null,
    token: null,
    isAuthenticated: false,
    isLoading: true,
  });

  // Wire up the Shopify session token getter to the API service
  useEffect(() => {
    if (isEmbedded) {
      api.getSessionToken = getSessionToken;
    }
    return () => {
      api.getSessionToken = null;
    };
  }, [isEmbedded, getSessionToken]);

  // Check auth once Shopify context is ready
  useEffect(() => {
    if (shopifyReady) {
      checkAuth();
    }
  }, [shopifyReady]);

  const checkAuth = useCallback(async () => {
    // For embedded mode: the token exchange already happened in ShopifyProvider
    // We just need to verify with /api/auth/me
    if (isEmbedded) {
      try {
        const response = await api.get<{ user: User }>('/auth/me');
        setState({
          user: response.data.user,
          token: null,
          isAuthenticated: true,
          isLoading: false,
        });
      } catch {
        setState({
          user: null,
          token: null,
          isAuthenticated: false,
          isLoading: false,
        });
      }
      return;
    }

    // Standalone mode: try cookie-based session first (Shopify OAuth),
    // then fall back to legacy token
    try {
      const response = await api.get<{ user: User }>('/auth/me');
      const user = response.data.user;

      setState({
        user,
        token: null,
        isAuthenticated: true,
        isLoading: false,
      });
      return;
    } catch {
      // Cookie/session not valid — try legacy token
    }

    // Legacy: check localStorage/sessionStorage token
    const token = localStorage.getItem(TOKEN_KEY) || sessionStorage.getItem(TOKEN_KEY);
    if (!token) {
      setState({
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
      });
      return;
    }

    try {
      const response = await api.get<{ user: User }>('/auth/me');
      const user = response.data.user;

      setState({
        user,
        token,
        isAuthenticated: true,
        isLoading: false,
      });
    } catch {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
      sessionStorage.removeItem(TOKEN_KEY);
      sessionStorage.removeItem(USER_KEY);

      setState({
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
      });
    }
  }, [isEmbedded]);

  const login = useCallback(async (email: string, password: string, remember = true) => {
    setState((prev) => ({ ...prev, isLoading: true }));

    try {
      const response = await api.post<{ token: string; user: User }>('/auth/login', { email, password, remember });
      const { token, user } = response.data;

      if (remember) {
        localStorage.setItem(TOKEN_KEY, token);
        localStorage.setItem(USER_KEY, JSON.stringify(user));
      } else {
        sessionStorage.setItem(TOKEN_KEY, token);
        sessionStorage.setItem(USER_KEY, JSON.stringify(user));
      }

      setState({
        user,
        token,
        isAuthenticated: true,
        isLoading: false,
      });
    } catch (error) {
      setState((prev) => ({ ...prev, isLoading: false }));
      throw error;
    }
  }, []);

  const logout = useCallback(() => {
    // Clear legacy tokens
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    sessionStorage.removeItem(TOKEN_KEY);
    sessionStorage.removeItem(USER_KEY);

    setState({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
    });
  }, []);

  return (
    <AuthContext.Provider
      value={{
        ...state,
        login,
        logout,
        checkAuth,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// =====================================================
// Hook
// =====================================================

export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
